import { Link } from "react-router-dom";
import { Factory, Heart, Target, Sparkles } from "lucide-react";
import { BRAND } from "@/data/products";

const AboutPage = () => {
  return (
    <main data-testid="about-page" className="max-w-7xl mx-auto px-6 md:px-12 py-20">
      {/* Hero */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-24">
        <div className="lg:col-span-6">
          <span className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">About Hospitality Hub</span>
          <h1 className="font-serif-display text-5xl lg:text-7xl mt-5 leading-[1.05]">
            A new standard for hotel <em className="text-[#D4AF37] not-italic font-medium">supplies.</em>
          </h1>
          <p className="mt-6 text-[#4A4A4A] leading-relaxed max-w-xl text-lg">
            Hospitality Hub was founded in 2023 with one simple belief: hotel owners deserve direct access to the factories making the very products their properties depend on, without the markups of three or four middlemen.
          </p>
        </div>
        <div className="lg:col-span-6">
          <img
            src="https://images.unsplash.com/photo-1590490360182-c33d57733427?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
            alt="Luxury hotel guest suite"
            className="w-full aspect-[4/3] object-cover"
          />
        </div>
      </section>

      <div className="hh-gold-divider mb-24" />

      {/* Story */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-24">
        <div className="lg:col-span-5">
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Our Story</p>
          <h2 className="font-serif-display text-4xl mt-4">From the factory floor to your front desk.</h2>
        </div>
        <div className="lg:col-span-7 space-y-5 text-[#4A4A4A] leading-relaxed">
          <p>
            We started Hospitality Hub after years of watching independent hotel owners and small franchisees pay premium prices for items the major chains buy at a fraction of the cost. The difference wasn't the product. It was the supply chain.
          </p>
          <p>
            By forging direct relationships with the same manufacturers supplying the world's largest hotel groups, and by keeping our own operation lean, we deliver Marriott-grade bed frames, Hilton-approved hospitality TVs, GE Zoneline PTACs, ETL-listed LED mirrors, and complete furniture packages at <strong className="text-[#1A1A1A]">factory-direct pricing</strong>.
          </p>
          <p>
            <em className="font-serif-display text-[#D4AF37] not-italic">What sets us apart is our factory-direct pricing</em>, allowing us to offer unmatched value to hoteliers across the US without compromising on quality.
          </p>
        </div>
      </section>

      {/* Values */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-24">
        {[
          { icon: Factory, title: "Direct Sourcing", desc: "We own our supplier relationships. No layers, no surprises." },
          { icon: Target, title: "Hotel-First", desc: "Built specifically for US hoteliers, from independents to global brands." },
          { icon: Sparkles, title: "Premium Quality", desc: "Brand-approved, certified, warrantied. Nothing less." },
          { icon: Heart, title: "Personal Service", desc: "A dedicated rep for every customer, every project." },
        ].map((v) => (
          <div key={v.title} className="bg-white border border-[#E5E5E5] p-7">
            <v.icon className="w-7 h-7 text-[#D4AF37]" />
            <h3 className="font-serif-display text-xl mt-4">{v.title}</h3>
            <p className="text-sm text-[#4A4A4A] mt-2 leading-relaxed">{v.desc}</p>
          </div>
        ))}
      </section>

      {/* Mission/Vision */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-24">
        <div className="border-l-2 border-[#D4AF37] pl-8">
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Mission</p>
          <h3 className="font-serif-display text-3xl mb-4">Best products. Best prices. Always.</h3>
          <p className="text-[#4A4A4A] leading-relaxed">
            To be the most trusted hospitality supplies partner for US hotels, delivering factory-direct value on every bed frame, mirror, PTAC, TV, and light fixture we ship.
          </p>
        </div>
        <div className="border-l-2 border-[#D4AF37] pl-8">
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Vision</p>
          <h3 className="font-serif-display text-3xl mb-4">A hub, not a middleman.</h3>
          <p className="text-[#4A4A4A] leading-relaxed">
            To become the single source US hoteliers turn to when opening a property, renovating a wing, or replacing aging equipment, because we earned that trust on every transaction.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="text-center bg-white border border-[#D4AF37]/30 p-14">
        <h2 className="font-serif-display text-3xl lg:text-4xl">Let's talk about your next project.</h2>
        <p className="text-[#4A4A4A] mt-3">Reach our team directly at <a href={`tel:${BRAND.phone}`} className="text-[#D4AF37] hover:underline">{BRAND.phone}</a></p>
        <Link to="/contact" data-testid="about-cta-contact" className="mt-7 inline-block bg-[#D4AF37] hover:bg-[#B5952F] text-white px-10 py-4 uppercase tracking-[0.2em] text-xs transition-colors">
          Get a Quote
        </Link>
      </section>
    </main>
  );
};

export default AboutPage;
